import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import styles from './AvgScoresDrillDown.module.scss';
import {
  StudentMetricsDashboardFilters,
  StudentSkillPerformanceSortByQueryParam,
} from '@/school-management/types/generate-report';
import PageLoader from '@/components/school-management/PageLoader/PageLoader';
import StudentMetricsDashboardTools from '@/components/school-management/StudentMetricsDashboard/StudentMetricsDashboardTools/StudentMetricsDashboardTools';
import GenericTable from '@/components/school-management/GenericTable/GenericTable';
import {
  getAvgScoresDrillDownData,
  getAvgScoresDrillDownColumnsData,
  StudentSkillPerformanceTable,
} from '../normalizeData';
import processing, { XlsxData, XlsxTable } from '@/school-management/utils/processing';

const emptyData: StudentSkillPerformanceTable[] = [];

const PAGE_SIZE = 50;

interface AvgScoresDrillDownProps {
  filters: StudentMetricsDashboardFilters;
  drillDownType: string;
  toolsContainer?: any;
}

const AvgScoresDrillDown: React.FC<AvgScoresDrillDownProps> = ({
  filters,
  drillDownType,
  toolsContainer,
}) => {
  const { t } = useAdminTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [data, setData] = useState<StudentSkillPerformanceTable[]>(emptyData);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>('firstname');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const getPageQueryParam = useCallback(() => {
    let sortKey: StudentSkillPerformanceSortByQueryParam = 'firstname';
    let sortBySkillId: number | undefined;

    switch (sortBy) {
      case 'name':
        sortKey = 'firstname';
        break;
      case 'classNameAndGrade':
        sortKey = 'classname';
        break;
      case 'currentLevel':
        sortKey = 'currentlevel';
        break;
      default:
        if (sortBy.startsWith('skillScore')) {
          sortKey = 'skill';
          sortBySkillId = Number(sortBy.replace('skillScore', ''));
        }
    }

    return {
      page: currentPage,
      pageSize: PAGE_SIZE,
      sortBy: sortKey,
      sortDirection: sortDirection,
      sortBySkillId: sortBySkillId,
    };
  }, [currentPage, sortBy, sortDirection]);

  useEffect(() => {
    let isActive: boolean = true;

    const initData = async () => {
      setIsLoading(true);

      let data: StudentSkillPerformanceTable[] = emptyData;
      let totalPages: number = 0;
      let totalCount: number = 0;

      try {
        const response = await getAvgScoresDrillDownData(filters, getPageQueryParam());

        if (response) {
          data = response.data;
          totalPages = response.paginationData.totalPages;
          totalCount = response.paginationData.totalCount;
        }
      } catch (er) {}

      if (!isActive) {
        return;
      }

      setData(data);
      setTotalPages(totalPages);
      setIsLoading(false);
      setIsFirstLoading(false);
      setTotalCount(totalCount);
    };

    initData();

    return () => {
      isActive = false;
    };
  }, [filters, getPageQueryParam]);

  const columns = useMemo(() => {
    return getAvgScoresDrillDownColumnsData(t);
  }, [t]);

  const tableName = useMemo(() => {
    return t(`studentMetricsDashboard.drillDown.skills.title.${drillDownType}`) || '';
  }, [t, drillDownType]);

  const onDownload = useCallback(async () => {
    console.log('Clicked to download student-skill-performance CSV');

    let skillsData: StudentSkillPerformanceTable[] = emptyData;

    try {
      const pageQueryParam = getPageQueryParam();
      const response = await getAvgScoresDrillDownData(filters, {
        sortBy: pageQueryParam.sortBy,
        sortDirection: pageQueryParam.sortDirection,
        sortBySkillId: pageQueryParam.sortBySkillId,
      });

      if (response) {
        skillsData = response.data;
      }
    } catch (er) {}

    const xlsxSkillsColumns = columns.filter(c => !!c.xlsxRender);
    const skillsHeaderRow = xlsxSkillsColumns.map(c => c.xlsxLabel || c.label);

    const xlsxData: XlsxData = {
      fileName: tableName,
      tables: [],
    };

    const skillTable: XlsxTable = {
      name: tableName,
      headerRow: [...skillsHeaderRow],
      data: [],
    };
    skillsData.forEach(l => {
      const row: any = {};
      xlsxSkillsColumns.forEach(col => {
        const formattedValue = col.xlsxRender?.((l as any)[col.key], l);
        row[col.xlsxLabel || col.label] = formattedValue;
      });
      skillTable.data.push(row);
    });
    xlsxData.tables.push(skillTable);

    processing.createXlsx(xlsxData);
  }, [filters, getPageQueryParam, columns, tableName]);

  if (isFirstLoading) {
    return <PageLoader title={t('studentMetricsDashboard.drillDown.skills.loadingData') || ''} />;
  }

  return (
    <div className={styles.skillsMode}>
      <StudentMetricsDashboardTools
        title={
          t('studentMetricsDashboard.drillDown.skills.totalStudents', {
            totalStudents: totalCount,
          }) || ''
        }
        toolsContainer={toolsContainer}
        totalStudents={totalCount}
      />
      <div className={styles.skillsDetailsCard}>
        <GenericTable
          columns={columns}
          data={data}
          rowKey={s => s.id}
          variant='compact'
          pageSize={PAGE_SIZE}
          disablePagination={false}
          showSortArrows={true}
          title={tableName}
          onDownload={onDownload}
          serverSideDownload={true}
          downloadFileName={tableName}
          serverSidePagination={true}
          totalPages={totalPages}
          totalCount={totalCount}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          serverSideSorting={true}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={(newSortBy: string | null, newSortDirection: 'asc' | 'desc' | null) => {
            if (newSortBy && newSortDirection) {
              setSortBy(newSortBy);
              setSortDirection(newSortDirection);
              setCurrentPage(1);
            }
          }}
          showLoader={isLoading}
        />
      </div>
    </div>
  );
};

export default AvgScoresDrillDown;
